// Grading a candidate (plan §6): melody fit is a FILTER, function is
// MEASURED by re-running the analysis on the result (a tritone sub must
// re-read as 'subV-I'; the local key must survive), and voice leading is a
// tiebreak — lineCost over a local window before vs after, Willmott's "a good
// sub keeps the guide line stepwise" made numeric. Surprisal is the corpus
// prior (surprisal.ts): reported, never optimised — the adventure dial
// selects a band, it does not reward being weird.

import { analyzeHarmony, functionalReading } from '../analysis';
import { pitchClassOf, sharpName } from '../notes';
import type { SequenceChord } from '../song';
import { lineCost, optimizeVoiceLeadingGlobal } from '../voiceLeading';
import { heldEnd, melodyIn } from './context';
import { melodyFitFor, unresolvedAlteredFor, worseFit } from './melodyFit';
import { meanSurprisal } from './surprisal';
import type { MelodyFit, ReharmCandidate, ReharmContext, ReharmScore } from './types';

type Proposal = Omit<ReharmCandidate, 'id' | 'score'>;

/** The written sequence with `proposal` substituted for the chord at `at`,
 *  the replacement chords laid inside the replaced chord's onsets. A slash
 *  bass travels with its chord: the analysis reads a hybrid from its bass
 *  (F/G is a V sus, not a IV), so dropping it here would grade the candidate
 *  as a different chord from the one it writes. */
export function sequenceWith(ctx: ReharmContext, proposal: Proposal): SequenceChord[] {
  const original = ctx.sequence[proposal.at];
  const out: SequenceChord[] = [];
  let beat = original.beat;
  const totalBeats = proposal.chords.reduce((s, c) => s + c.beats, 0) || 1;
  ctx.sequence.forEach((chord, i) => {
    if (i !== proposal.at) {
      out.push(chord);
      return;
    }
    proposal.chords.forEach((spec, k) => {
      // The last piece inherits the original's held duration (a chord that
      // rang through blank bars still does); earlier pieces take their share.
      const last = k === proposal.chords.length - 1;
      const share = (spec.beats / totalBeats) * ctx.chordBeats[proposal.at];
      out.push({
        id: `${original.id}~${k}`,
        root: sharpName(pitchClassOf(spec.root)),
        displayRoot: spec.root,
        chordType: spec.chordType,
        symbol: `${spec.root}${spec.chordType}${spec.bass ? `/${spec.bass}` : ''}`,
        barIndex: original.barIndex,
        beat,
        durationBeats: last
          ? Math.max(share, original.durationBeats - (beat - original.beat))
          : share,
        stringSet: original.stringSet,
        ...(spec.bass ? { bass: spec.bass } : {}),
      });
      beat += share;
    });
  });
  return out;
}

export interface ScoreOptions {
  /** Measure voice-leading cost (runs the global optimiser twice per
   *  candidate; the corpus sweep turns it off). Default true. */
  voiceLeading?: boolean;
}

export function scoreCandidate(
  ctx: ReharmContext,
  proposal: Proposal,
  options: ScoreOptions = {},
): ReharmScore {
  // Melody: each replacement chord against what sounds over ITS sub-span —
  // for the last piece, everything to where the replaced chord stops
  // sounding, since it rings on through any bars with no symbol just as the
  // original did (sequenceWith gives it that duration). A dominant
  // "resolves" when what follows it — the next piece of the candidate, or
  // the tune's next chord after the last piece — sits a perfect fifth below
  // (Felts's ♭9 rule reads that, see melodyFit.ts), read as HEARD: a hybrid
  // next door is its bass (Gm7/C is a C sus, so D7 → Gm7/C does not resolve
  // and D7 → F/G does), the reading the moves and the analysis use.
  const after = sequenceWith(ctx, proposal);
  const hasMelody = ctx.melody !== null;
  let melodyFit: MelodyFit = hasMelody ? 'chord-tone' : 'no-melody';
  let unresolvedAltered = 0;
  let cursor = ctx.chordStarts[proposal.at];
  const totalBeats = proposal.chords.reduce((s, c) => s + c.beats, 0) || 1;
  const end = heldEnd(ctx, proposal.at);
  proposal.chords.forEach((spec, k) => {
    const share = (spec.beats / totalBeats) * ctx.chordBeats[proposal.at];
    const last = k === proposal.chords.length - 1;
    const sung = melodyIn(ctx, cursor, last ? Math.max(end, cursor + share) : cursor + share);
    const nextAt = proposal.at + k + 1;
    const dominantResolves =
      nextAt < after.length &&
      (functionalReading(after, nextAt).rootPc - pitchClassOf(spec.root) + 12) % 12 === 5;
    melodyFit = worseFit(
      melodyFit,
      melodyFitFor(spec.root, spec.chordType, sung, hasMelody, {
        bass: spec.bass,
        dominantResolves,
      }),
    );
    unresolvedAltered += unresolvedAlteredFor(spec.root, spec.chordType, sung);
    cursor += share;
  });

  // Function: re-analyse the result and read the region + cells at the span.
  const analysis = analyzeHarmony(after, ctx.globalKey);
  const before = ctx.analysis.regions.find((r) => proposal.at >= r.from && proposal.at <= r.to);
  const spanFrom = proposal.at;
  const spanTo = proposal.at + proposal.chords.length - 1;
  const afterRegion = analysis.regions.find((r) => spanTo >= r.from && spanFrom <= r.to);
  const functionKept =
    !!before &&
    !!afterRegion &&
    before.tonicPc === afterRegion.tonicPc &&
    before.minor === afterRegion.minor;
  const cellsAfter = analysis.cells
    .filter((cell) => cell.to >= spanFrom && cell.from <= spanTo + 1)
    .map((cell) => cell.kind);

  // The corpus prior: the transitions INTO, THROUGH and OUT OF the span, after
  // and before, in the local-key romans the analysis assigns.
  const surprisal = meanSurprisal(analysis.romans.slice(Math.max(0, spanFrom - 1), spanTo + 2));
  const surprisalBefore = meanSurprisal(
    ctx.analysis.romans.slice(Math.max(0, proposal.at - 1), proposal.at + 2),
  );

  let voiceLeading = 0;
  if (options.voiceLeading !== false) {
    const lo = Math.max(0, proposal.at - 2);
    const hiBefore = Math.min(ctx.sequence.length, proposal.at + 3);
    const hiAfter = Math.min(after.length, spanTo + 3);
    const costOf = (seq: SequenceChord[]) => {
      const voiced = optimizeVoiceLeadingGlobal(seq);
      return voiced ? lineCost(voiced) : Number.POSITIVE_INFINITY;
    };
    const costBefore = costOf(ctx.sequence.slice(lo, hiBefore));
    const costAfter = costOf(after.slice(lo, hiAfter));
    voiceLeading =
      Number.isFinite(costAfter) && Number.isFinite(costBefore) ? costAfter - costBefore : 0;
  }

  // Harmonic rhythm: chords per bar over the replaced chord's own span.
  const pace =
    proposal.chords.length / (ctx.chordBeats[proposal.at] / (ctx.barBeats[proposal.at] || 4));

  return {
    melodyFit,
    functionKept,
    unresolvedAltered,
    cellsAfter,
    voiceLeading,
    surprisal,
    surprisalBefore,
    pace,
  };
}
